import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { getUserIdByUsername, trimUsername } from '../lib/usernames';
import './Dashboard.css';

function avatarColor(id) {
  const palette = ['#7c3aed', '#2563eb', '#059669', '#b45309', '#db2777', '#0891b2', '#dc2626', '#65a30d'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

const FriendsPanel = ({ user, friends, onRefresh, onlineUserIds, onFriendSelect }) => {
  const [addUsername, setAddUsername] = useState('');
  const [addStatus, setAddStatus] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddFriend = async () => {
    const username = trimUsername(addUsername);
    if (!username) return;
    setAdding(true);
    setAddStatus('');

    let profileId;
    try {
      profileId = await getUserIdByUsername(username);
    } catch (lookupError) {
      setAdding(false);
      setAddStatus(lookupError.message);
      return;
    }

    if (!profileId) {
      setAddStatus('We could not find a user with that username.');
      setAdding(false);
      return;
    }

    if (profileId === user.id) {
      setAddStatus("You can't add yourself.");
      setAdding(false);
      return;
    }

    const { error } = await supabase.from('friends').insert({
      requester_id: user.id,
      addressee_id: profileId,
    });

    setAdding(false);
    if (error) {
      setAddStatus(error.code === '23505' ? 'Request already sent or you are already friends.' : error.message);
    } else {
      setAddStatus('Friend request sent!');
      setAddUsername('');
      onRefresh();
    }
  };

  const handleRemove = async (friendRow) => {
    await supabase.from('friends').delete().eq('id', friendRow.id);
    onRefresh();
  };

  const handleCancelRequest = async (friendRow) => {
    await supabase.from('friends').delete().eq('id', friendRow.id);
    onRefresh();
  };

  // Sent requests: pending where I am the requester
  const sentRequests = friends.filter(
    f => f.status === 'pending' && f.requester_id === user.id
  );

  const getOtherProfile = (f) => {
    return f.requester_id === user.id ? f.addressee : f.requester;
  };

  const acceptedFriends = friends
    .filter(f => f.status === 'accepted')
    .slice()
    .sort((a, b) => {
      const aProfile = getOtherProfile(a);
      const bProfile = getOtherProfile(b);
      const aName = (aProfile?.display_name || aProfile?.username || '').toLocaleLowerCase();
      const bName = (bProfile?.display_name || bProfile?.username || '').toLocaleLowerCase();
      return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
    });

  return (
    <div className="dash-panel">
      <h1 className="dash-panel-title">Friends</h1>

      {/* Add Friend */}
      <div className="dash-section">
        <p className="dash-section-label">Add Friend</p>
        <div className="dash-create-form">
          <input
            className="dash-input"
            value={addUsername}
            onChange={e => setAddUsername(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddFriend()}
            placeholder="Username…"
          />
          <button className="dash-btn" onClick={handleAddFriend} disabled={adding || !addUsername.trim()}>
            {adding ? 'Sending…' : 'Add'}
          </button>
        </div>
        {addStatus && (
          <p className="dash-error" style={{ color: addStatus.includes('sent') ? '#34d399' : undefined }}>
            {addStatus}
          </p>
        )}
      </div>

      {/* Friends List */}
      <div className="dash-section">
        <p className="dash-section-label">Friends ({acceptedFriends.length})</p>
        {acceptedFriends.length === 0 ? (
          <p className="dash-empty">No friends yet.</p>
        ) : (
          acceptedFriends.map(f => {
            const p = getOtherProfile(f);
            const name = p?.display_name || p?.username || 'Unknown';
            const isOnline = onlineUserIds?.has(p?.id);
            return (
              <div key={f.id} className="dash-person-card">
                <div
                  className="dash-person-avatar"
                  style={{ background: avatarColor(p?.id || f.id) }}
                >
                  {name.charAt(0).toUpperCase()}
                </div>
                <div className="dash-person-info">
                  <div className="dash-person-name-row">
                    <button
                      className="dash-person-link"
                      type="button"
                      onClick={() => onFriendSelect?.(p?.id)}
                    >
                      {name}
                    </button>
                    <span className={`dash-person-presence${isOnline ? ' is-online' : ''}`}>
                      {isOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  {p?.username && <div className="dash-person-handle">@{p.username}</div>}
                </div>
                <div className="dash-card-actions">
                  <button
                    className="dash-btn dash-btn-secondary"
                    style={{ fontSize: 12 }}
                    onClick={() => handleRemove(f)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Sent Requests */}
      {sentRequests.length > 0 && (
        <div className="dash-section">
          <p className="dash-section-label">Sent Requests</p>
          {sentRequests.map(f => {
            const p = f.addressee;
            const name = p?.display_name || p?.username || 'Unknown';
            return (
              <div key={f.id} className="dash-person-card">
                <div
                  className="dash-person-avatar"
                  style={{ background: avatarColor(f.addressee_id) }}
                >
                  {name.charAt(0).toUpperCase()}
                </div>
                <div className="dash-person-info">
                  <div className="dash-person-name">{name}</div>
                  {p?.username && <div className="dash-person-handle">@{p.username}</div>}
                </div>
                <div className="dash-card-actions">
                  <button
                    className="dash-btn dash-btn-secondary"
                    style={{ fontSize: 12 }}
                    onClick={() => handleCancelRequest(f)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FriendsPanel;
